import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import {
  CircleX,
  Drum,
  PartyPopper,
  Sparkles,
  Spotlight,
  type LucideIcon,
} from "lucide-react"
import { useState } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Id } from "../../../../convex/_generated/dataModel"
import { getGameModule, gameRegistry } from "@/games/registry"
import { ALL_DECADES, lcConfigSchema } from "@/games/lyrically-challenged/schemas"
import { analyticsEvents } from "@/lib/analytics/events"
import { trackEvent } from "@/lib/analytics/posthog"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"
import { isEarlyGameStart } from "@/lib/gameStart"

export const Route = createFileRoute("/_authed/host/games/$gameId")({
  component: GameDetail,
})

type LiveEffectKind = "confetti" | "sparkles" | "spotlight" | "drumroll"

function GameDetail() {
  const { gameId } = Route.useParams()
  const game = useQuery(api.games.getById, { gameId: gameId as Id<"games"> })
  const rounds = useQuery(
    api.rounds.listForGame,
    game?._id ? { gameId: game._id } : "skip",
  )
  const event = useQuery(
    api.events.getById,
    game?.eventId ? { eventId: game.eventId } : "skip",
  )
  const eventGames = useQuery(
    api.games.listForEvent,
    game?.eventId ? { eventId: game.eventId } : "skip",
  )
  const updateEvent = useMutation(api.events.update)
  const updateGame = useMutation(api.games.update)
  const createGame = useMutation(api.games.create)
  const soloEnabled = useWildGuessSoloEnabled()
  const navigate = useNavigate()
  const [nextGameCreating, setNextGameCreating] = useState(false)
  const [nextGameError, setNextGameError] = useState<string | null>(null)

  if (game === undefined) {
    return <p className="text-sm text-[color:var(--muted-foreground)]">Loading…</p>
  }
  if (game === null) {
    return (
      <p className="text-sm text-[color:var(--muted-foreground)]">
        No game with that id.
      </p>
    )
  }
  if (game.gameType === "solo-trivia" && !soloEnabled) {
    return (
      <div className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 p-8 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--muted-foreground)]">
          Feature hidden
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          Wild Guess Solo is behind a flag.
        </h1>
        <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-[color:var(--muted-foreground)]">
          This console is currently focused on Lyrically Challenged. Enable the
          PostHog flag to manage Solo games again.
        </p>
        <Link
          to="/host"
          className="mt-5 inline-flex rounded-md border border-[color:var(--border)] px-3 py-1.5 text-xs hover:bg-[color:var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
        >
          Back to host
        </Link>
      </div>
    )
  }

  const currentGame = game

  let module
  try {
    module = getGameModule(currentGame.gameType)
  } catch {
    return (
      <p className="text-sm text-[color:var(--muted-foreground)]">
        Unknown game type: <code>{currentGame.gameType}</code>
      </p>
    )
  }
  const moduleLabel =
    gameRegistry[currentGame.gameType as keyof typeof gameRegistry]?.label ??
    currentGame.gameType
  const AdminLive = module.AdminLiveComponent

  async function handleEventStatusChange(status: EventStatus) {
    if (!currentGame.eventId) return
    await updateEvent({ eventId: currentGame.eventId, status })
    trackEvent(analyticsEvents.hostEventUpdated, {
      eventId: currentGame.eventId,
      status,
      changeType: "run_console_lifecycle",
    })
    trackEvent(analyticsEvents.lcGameStatusChanged, {
      gameId,
      gameType: currentGame.gameType,
      fromStatus: currentGame.status,
      toStatus: status,
    })
  }

  async function handleGameStatusChange(status: GameStatus, confirmedEarlyStartAt?: number) {
    await updateGame({ gameId: currentGame._id, status, confirmedEarlyStartAt })
    trackEvent(analyticsEvents.lcGameStatusChanged, {
      gameId,
      gameType: currentGame.gameType,
      fromStatus: currentGame.status,
      toStatus: status,
      changeType: "run_console_game_lifecycle",
    })
  }

  async function handleCreateNextGame() {
    if (!currentGame.eventId || currentGame.gameType !== "lyrically-challenged") {
      return
    }
    setNextGameCreating(true)
    setNextGameError(null)
    try {
      const reopensEvent = event?.status === "completed"
      const config = lcConfigSchema.parse({
        decadesEnabled: ALL_DECADES,
      })
      const nextGameId = await createGame({
        eventId: currentGame.eventId,
        gameType: "lyrically-challenged",
        config,
      })
      trackEvent(analyticsEvents.lcGameCreated, {
        eventId: currentGame.eventId,
        gameId: nextGameId,
        decadesEnabledCount: config.decadesEnabled.length,
        reopensEvent,
        source: "completed_game_console",
      })
      await navigate({
        to: "/host/games/$gameId",
        params: { gameId: nextGameId },
      })
    } catch (err) {
      setNextGameError(err instanceof Error ? err.message : String(err))
    } finally {
      setNextGameCreating(false)
    }
  }

  const canCreateNextGame =
    currentGame.eventId &&
    currentGame.gameType === "lyrically-challenged" &&
    currentGame.status === "completed" &&
    event !== undefined &&
    event !== null &&
    event.status !== "cancelled"

  return (
    <div className="space-y-6">
      <div>
        {currentGame.eventId && (
          <Link
            to="/host/events/$eventId"
            params={{ eventId: currentGame.eventId }}
            className="rounded-sm text-xs text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            ← Back to event
          </Link>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {moduleLabel}
          </h1>
          <div className="flex items-center gap-3">
            <Link
              to="/host/magic-numbers"
              className="rounded-sm text-xs text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
              Magic #s
            </Link>
            <Link
              to="/host/games/$gameId/stats"
              params={{ gameId: currentGame._id }}
              className="rounded-sm text-xs text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
              Stats →
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] p-4">
        <RunLifecyclePanel
          game={currentGame}
          event={event}
          onGameStatusChange={handleGameStatusChange}
        />
        <LiveEffectsPanel gameId={currentGame._id} />
      </div>

      {canCreateNextGame ? (
        <section className="console-panel rounded-[1.35rem] border-primary/25 bg-primary/[0.08] px-5 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="console-kicker text-primary">Next game</p>
              <h2 className="mt-1 text-xl font-bold tracking-tight">
                Run another Lyrically Challenged game.
              </h2>
              <p className="mt-1 text-sm leading-6 text-[color:var(--muted-foreground)]">
                Create a fresh game, then open registration when the room is ready.
              </p>
              {nextGameError ? (
                <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
                  {nextGameError}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => void handleCreateNextGame()}
              disabled={nextGameCreating}
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-[0_18px_40px_-24px_rgba(67,65,201,0.95)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {nextGameCreating ? "Creating…" : "Create next game"}
            </button>
          </div>
        </section>
      ) : null}

      <section id="run" className="space-y-4 scroll-mt-24">
        <ConsoleSectionHeader
          kicker="Run"
          title="Live game"
          detail="Manage teams, rounds, scoring, and final standings."
        />
        <AdminLive gameId={currentGame._id} />
        <EventCloseoutPanel
          game={currentGame}
          event={event}
          rounds={rounds}
          eventGames={eventGames}
          onEventStatusChange={handleEventStatusChange}
        />
      </section>
    </div>
  )
}

type EventStatus = "scheduled" | "ready" | "active" | "completed" | "cancelled"
type GameStatus = "scheduled" | "ready" | "active" | "completed" | "cancelled"
type RoundStatus = "pending" | "active" | "completed"
type CloseoutRound = {
  index: number
  status: RoundStatus
}
type CloseoutGame = {
  _id: Id<"games">
  _creationTime: number
  gameType: string
  status: GameStatus
}

export function RunLifecyclePanel({
  game,
  event,
  onGameStatusChange,
}: {
  game: { eventId?: Id<"events">; status: GameStatus; startsAt?: number }
  event:
    | { status: EventStatus; startsAt: number; name: string }
    | null
    | undefined
  onGameStatusChange: (status: GameStatus, confirmedEarlyStartAt?: number) => Promise<void>
}) {
  const [busyStatus, setBusyStatus] = useState<GameStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const copy = getGameLifecycleCopy(game.status, event?.status)

  async function changeStatus(nextStatus: GameStatus) {
    if (nextStatus === "cancelled" && !window.confirm(
      "Cancel this game? Registration and gameplay will close. Teams and scores will be kept.",
    )) return
    let confirmedEarlyStartAt: number | undefined
    const startsAt = game.startsAt ?? event?.startsAt
    if (nextStatus === "active" && isEarlyGameStart(startsAt, Date.now())) {
      const scheduledTime = new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium", timeStyle: "short",
      }).format(startsAt)
      if (!window.confirm(
        `This game is scheduled for ${scheduledTime}. Start gameplay now? Players will see it as live, and it will close after 4 hours without host activity.`,
      )) return
      confirmedEarlyStartAt = startsAt
    }
    setBusyStatus(nextStatus)
    setError(null)
    try {
      if (confirmedEarlyStartAt !== undefined) {
        await onGameStatusChange(nextStatus, confirmedEarlyStartAt)
      } else {
        await onGameStatusChange(nextStatus)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyStatus(null)
    }
  }

  let primaryAction: { status: GameStatus; label: string } | null = null
  const eventClosed =
    event?.status === "completed" || event?.status === "cancelled"
  if (!eventClosed && game.status === "scheduled") {
    primaryAction = { status: "ready", label: "Open registration" }
  } else if (
    !eventClosed &&
    game.status === "ready"
  ) {
    primaryAction = { status: "active", label: "Start game" }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
            Game state
          </p>
          <p className="text-sm font-semibold text-[color:var(--foreground)]">
            {copy.title}
          </p>
          <p className="mt-1 text-xs leading-5 text-[color:var(--muted-foreground)]">
            {copy.detail}
          </p>
        </div>
        <div className="flex-1" />
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={game.status} />
          {primaryAction && game.eventId && (
            <button
              type="button"
              onClick={() => void changeStatus(primaryAction.status)}
              disabled={busyStatus !== null}
              className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {busyStatus === primaryAction.status ? "Updating…" : primaryAction.label}
            </button>
          )}
          {!eventClosed && !isTerminalGameStatus(game.status) && (
            <button
              type="button"
              onClick={() => void changeStatus("cancelled")}
              disabled={busyStatus !== null}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 disabled:opacity-50"
            >
              <CircleX size={14} aria-hidden="true" />
              {busyStatus === "cancelled" ? "Canceling..." : "Cancel game"}
            </button>
          )}
        </div>
      </div>
      {error && (
        <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
          {error}
        </p>
      )}
    </div>
  )
}

function EventCloseoutPanel({
  game,
  event,
  rounds,
  eventGames,
  onEventStatusChange,
}: {
  game: { _id: Id<"games">; eventId?: Id<"events">; status: GameStatus }
  event:
    | { status: EventStatus; startsAt: number; name: string }
    | null
    | undefined
  rounds: CloseoutRound[] | undefined
  eventGames: CloseoutGame[] | undefined
  onEventStatusChange: (status: EventStatus) => Promise<void>
}) {
  const [busyStatus, setBusyStatus] = useState<EventStatus | null>(null)
  const status = event?.status ?? game.status

  if (!game.eventId || status === "completed" || status === "cancelled") {
    return null
  }

  const unfinishedSummary = getEventCloseoutSummary(rounds, eventGames, game)
  const canCompleteEvent = status === "active" && unfinishedSummary === null

  async function changeStatus(nextStatus: EventStatus) {
    if (!confirmCloseoutStatus(nextStatus, unfinishedSummary)) return

    setBusyStatus(nextStatus)
    try {
      await onEventStatusChange(nextStatus)
    } finally {
      setBusyStatus(null)
    }
  }

  return (
    <section
      aria-label="Event closeout"
      className="rounded-md border border-red-200 bg-red-50/50 p-4"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-red-700">
            Event closeout
          </p>
          <h2 className="mt-1 text-base font-semibold text-red-950">
            Finish or cancel only when the room is done.
          </h2>
          <p className="mt-1 text-xs leading-5 text-red-900/75">
            These actions close the event for players. Completion unlocks after
            every game in the event is complete or canceled.
          </p>
          {status === "active" && unfinishedSummary ? (
            <p className="mt-2 rounded-lg border border-red-200 bg-white/60 px-3 py-2 text-xs leading-5 text-red-900">
              {unfinishedSummary}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canCompleteEvent && (
            <button
              type="button"
              onClick={() => void changeStatus("completed")}
              disabled={busyStatus !== null}
              className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {busyStatus === "completed" ? "Completing…" : "Complete event"}
            </button>
          )}
          <button
            type="button"
            onClick={() => void changeStatus("cancelled")}
            disabled={busyStatus !== null}
            className="rounded-full border border-red-300 bg-white/70 px-3 py-2 text-xs font-semibold text-red-700 transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/40 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {busyStatus === "cancelled" ? "Canceling…" : "Cancel event"}
          </button>
        </div>
      </div>
    </section>
  )
}

function getEventCloseoutSummary(
  rounds: CloseoutRound[] | undefined,
  eventGames: CloseoutGame[] | undefined,
  currentGame: { _id: Id<"games">; status: GameStatus },
) {
  if (eventGames === undefined) {
    return "Event games are still loading."
  }

  const summaries: string[] = []
  const roundSummary = isTerminalGameStatus(currentGame.status)
    ? null
    : getUnfinishedRoundSummary(rounds)
  if (roundSummary) summaries.push(roundSummary)

  const otherUnfinishedGames = eventGames.filter(
    (game) =>
      game._id !== currentGame._id && !isTerminalGameStatus(game.status),
  )
  if (otherUnfinishedGames.length > 0) {
    const labels = otherUnfinishedGames
      .slice()
      .sort((a, b) => a._creationTime - b._creationTime)
      .map(formatUnfinishedGameLabel)
      .join(", ")
    summaries.push(
      `${otherUnfinishedGames.length} other unfinished game${
        otherUnfinishedGames.length === 1 ? "" : "s"
      }: ${labels}.`,
    )
  }

  if (
    summaries.length === 0 &&
    !isTerminalGameStatus(currentGame.status)
  ) {
    summaries.push(`Current game is ${formatStatusForSentence(currentGame.status)}.`)
  }

  return summaries.length > 0 ? summaries.join("\n") : null
}

function getUnfinishedRoundSummary(rounds: CloseoutRound[] | undefined) {
  if (rounds === undefined) {
    return "Round status is still loading."
  }

  const unfinishedRounds = rounds.filter((round) => round.status !== "completed")
  if (unfinishedRounds.length === 0) {
    return null
  }

  const labels = unfinishedRounds
    .slice()
    .sort((a, b) => a.index - b.index)
    .map(formatUnfinishedRoundLabel)
    .join(", ")

  return `${unfinishedRounds.length} unfinished round${
    unfinishedRounds.length === 1 ? "" : "s"
  }: ${labels}.`
}

function formatUnfinishedRoundLabel(round: CloseoutRound) {
  const statusLabel = round.status === "active" ? "live" : round.status
  return `R${round.index + 1} ${statusLabel}`
}

function formatUnfinishedGameLabel(game: CloseoutGame) {
  const label =
    gameRegistry[game.gameType as keyof typeof gameRegistry]?.label ??
    game.gameType
  return `${label} ${formatStatusForSentence(game.status)}`
}

function formatStatusForSentence(status: GameStatus) {
  if (status === "active") return "live"
  if (status === "cancelled") return "canceled"
  return status
}

function isTerminalGameStatus(status: GameStatus) {
  return status === "completed" || status === "cancelled"
}

function confirmCloseoutStatus(
  nextStatus: EventStatus,
  unfinishedSummary: string | null,
) {
  if (nextStatus !== "completed" && nextStatus !== "cancelled") {
    return true
  }

  const action =
    nextStatus === "completed" ? "Complete this event" : "Cancel this event"
  const consequence =
    nextStatus === "completed"
      ? "This will close gameplay and move players toward final standings."
      : "Players will stop seeing this event as joinable."

  if (unfinishedSummary && nextStatus === "completed") {
    window.alert(unfinishedSummary)
    return false
  }

  if (unfinishedSummary) {
    return window.confirm(`${unfinishedSummary}\n\n${action}?\n\n${consequence}`)
  }

  return window.confirm(`${action}?\n\n${consequence}`)
}

function getGameLifecycleCopy(status: GameStatus, eventStatus?: EventStatus) {
  if (eventStatus === "completed" || eventStatus === "cancelled") {
    return {
      title: "Event closed",
      detail: "This event is closed for players. Reopen or create a new event to run more games.",
    }
  }

  switch (status) {
    case "scheduled":
      return {
        title: "Upcoming game",
        detail: "Open registration when players should be able to create or join teams.",
      }
    case "ready":
      return {
        title: "Team registration open",
        detail: "Players can create or join teams. Start this game when the room is ready.",
      }
    case "active":
      return {
        title: "Game is live",
        detail: "Player apps are in gameplay mode. Start the next round from the round queue.",
      }
    case "completed":
      return {
        title: "Game complete",
        detail: "Rounds are closed. Use the final reveal when standings are ready.",
      }
    case "cancelled":
      return {
        title: "Game canceled",
        detail: "Players will stop seeing this game as joinable.",
      }
  }
}

function StatusBadge({ status }: { status: EventStatus | GameStatus }) {
  const tone: Record<string, string> = {
    scheduled:
      "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]",
    ready: "border-amber-300 bg-amber-50 text-amber-800",
    active: "border-emerald-300 bg-emerald-50 text-emerald-800",
    completed:
      "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]",
    cancelled: "border-red-200 bg-red-50 text-red-700",
  }
  return (
    <span
      className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${tone[status]}`}
    >
      {status === "cancelled" ? "canceled" : status}
    </span>
  )
}

const LIVE_EFFECTS: ReadonlyArray<{
  kind: LiveEffectKind
  label: string
  Icon: LucideIcon
}> = [
  { kind: "confetti", label: "Confetti", Icon: PartyPopper },
  { kind: "sparkles", label: "Sparkles", Icon: Sparkles },
  { kind: "spotlight", label: "Spotlight", Icon: Spotlight },
  { kind: "drumroll", label: "Drumroll", Icon: Drum },
]

function LiveEffectsPanel({ gameId }: { gameId: Id<"games"> }) {
  const trigger = useMutation(api.liveEffects.trigger)
  const [busyKind, setBusyKind] = useState<LiveEffectKind | null>(null)
  const [lastKind, setLastKind] = useState<LiveEffectKind | null>(null)

  async function handleTrigger(kind: LiveEffectKind) {
    setBusyKind(kind)
    try {
      await trigger({ gameId, kind })
      setLastKind(kind)
    } finally {
      setBusyKind(null)
    }
  }

  return (
    <div className="mt-4 border-t border-[color:var(--border)] pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)]">
          FX
        </p>
        {LIVE_EFFECTS.map(({ kind, label, Icon }) => (
          <button
            key={kind}
            type="button"
            onClick={() => void handleTrigger(kind)}
            disabled={busyKind !== null}
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-1.5 text-xs font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--surface-soft)] disabled:cursor-wait disabled:opacity-55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            <Icon size={14} strokeWidth={2.5} />
            {busyKind === kind ? "Sending" : label}
          </button>
        ))}
        {lastKind && (
          <span className="ml-auto text-xs text-[color:var(--muted-foreground)]">
            Sent {LIVE_EFFECTS.find((effect) => effect.kind === lastKind)?.label}
          </span>
        )}
      </div>
    </div>
  )
}

function ConsoleSectionHeader({
  kicker,
  title,
  detail,
}: {
  kicker: string
  title: string
  detail: string
}) {
  return (
    <div className="console-panel rounded-[1.35rem] px-5 py-4">
      <p className="console-kicker">{kicker}</p>
      <h2 className="mt-1 text-2xl font-bold tracking-tight">{title}</h2>
      <p className="mt-1 text-sm leading-6 text-[color:var(--muted-foreground)]">
        {detail}
      </p>
    </div>
  )
}
